import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, ROLE_LABELS } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Save, Download, Plus, Trash2, UploadCloud, Loader2, ArrowLeft } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { MonthlyAnalysis } from "../components/MonthlyAnalysis";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Grid({ block, value, onChange }) {
  const { t } = useLang();
  const cols = block.cols;
  if (block.rows) {
    const v = value || {};
    const set = (row, k, val) => onChange({ ...v, [row]: { ...(v[row] || {}), [k]: val } });
    return (
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F1F5F9]"><th className="p-2 text-left text-xs font-bold text-slate-600"></th>
            {cols.map((c) => <th key={c.k} className="p-2 text-left text-xs font-bold text-slate-600">{c.label}</th>)}</tr></thead>
          <tbody>{block.rows.map((row) => (
            <tr key={row} className="border-t border-slate-100">
              <td className="p-2 text-xs font-medium text-slate-600 bg-[#F8FAFC] w-1/4">{row}</td>
              {cols.map((c) => <td key={c.k} className="p-1"><Input className="h-8 text-xs" value={(v[row] || {})[c.k] || ""} onChange={(e) => set(row, c.k, e.target.value)} /></td>)}
            </tr>))}</tbody>
        </table>
      </div>
    );
  }
  const rows = Array.isArray(value) ? value : [];
  const setRow = (i, k, val) => { const n = rows.map((r, j) => (j === i ? { ...r, [k]: val } : r)); onChange(n); };
  const addRow = () => onChange([...rows, {}]);
  const delRow = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F1F5F9]">{cols.map((c) => <th key={c.k} className="p-2 text-left text-xs font-bold text-slate-600">{c.label}</th>)}<th className="w-8"></th></tr></thead>
          <tbody>{rows.length === 0 ? (
            <tr><td colSpan={cols.length + 1} className="p-3 text-center text-xs text-slate-500">{t("Aucune ligne. Cliquez « Ajouter une ligne ».")}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map((c) => <td key={c.k} className="p-1"><Input className="h-8 text-xs" value={r[c.k] || ""} onChange={(e) => setRow(i, c.k, e.target.value)} /></td>)}
              <td className="p-1 text-center"><button onClick={() => delRow(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></td>
            </tr>))}</tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" className="mt-2 rounded-md" onClick={addRow}><Plus className="h-3.5 w-3.5 mr-1" /> {t("Ajouter une ligne")}</Button>
    </div>
  );
}

function Photos({ photos, onChange }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("token");
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/monthly-reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(photos || []), { path: res.data.path, caption: "", date: "", gps: "" }]);
      toast.success(t("Photo ajoutée."));
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const upd = (i, k, val) => onChange(photos.map((p, j) => (j === i ? { ...p, [k]: val } : p)));
  const del = (i) => onChange(photos.filter((_, j) => j !== i));
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
        {(photos || []).map((p, i) => (
          <div key={i} className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={`mr-photo-${i}`}>
            <img src={`${BACKEND}/api/mr-files/${p.path}?auth=${token}`} alt="" className="w-full h-36 object-cover bg-slate-100" />
            <div className="p-2 space-y-1.5">
              <Input className="h-7 text-xs" placeholder={t("Légende")} value={p.caption || ""} onChange={(e) => upd(i, "caption", e.target.value)} />
              <div className="flex gap-1.5">
                <Input className="h-7 text-xs" type="date" value={p.date || ""} onChange={(e) => upd(i, "date", e.target.value)} />
                <Input className="h-7 text-xs" placeholder={t("GPS")} value={p.gps || ""} onChange={(e) => upd(i, "gps", e.target.value)} />
              </div>
              <button onClick={() => del(i)} className="text-xs text-red-500 hover:underline">{t("Retirer")}</button>
            </div>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#0F172A] text-white text-sm font-semibold cursor-pointer hover:bg-[#1E293B]" data-testid="mr-photo-upload">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {t("Téléverser une photo")}
        <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
      </label>
    </div>
  );
}

export default function MonthlyReportForm() {
  const { id } = useParams();
  const isNew = id === "new";
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ volet: "epi", province: "", health_zones: "", month: "", year: new Date().getFullYear(), report_no: "", status: "draft" });
  const [data, setData] = useState({});
  const [photos, setPhotos] = useState([]);
  const [sections, setSections] = useState([]);
  const [rid, setRid] = useState(isNew ? null : id);
  const [saving, setSaving] = useState(false);

  const loadTemplate = useCallback(async (volet) => {
    const res = await api.get("/monthly-reports/template", { params: { volet } });
    setSections(res.data.sections);
  }, []);

  useEffect(() => {
    (async () => {
      if (isNew) {
        if (!user) return;
        const volet = user.volet || "epi";
        const roleLabel = ROLE_LABELS[user.role] || user.job_title || "";
        const affectation = `${user.province || ""}${user.health_zone ? " · " + user.health_zone : ""}`;
        setMeta((m) => ({ ...m, volet, province: user.province || "", health_zones: user.health_zone || "" }));
        setData({ fiche: { nom: user.name || "", role: roleLabel, affectation, projet: user.project || user.organization || "", point_focal: user.point_focal || "" } });
        await loadTemplate(volet);
        return;
      }
      const res = await api.get(`/monthly-reports/${id}`);
      const d = res.data;
      setMeta({ volet: d.volet, province: d.province, health_zones: d.health_zones, month: d.month, year: d.year, report_no: d.report_no, status: d.status });
      setData(d.data || {}); setPhotos(d.photos || []);
      await loadTemplate(d.volet);
    })();
  }, [id, isNew, loadTemplate, user]);

  const changeVolet = async (v) => { setMeta({ ...meta, volet: v }); await loadTemplate(v); };
  const setD = (key, val) => setData((prev) => ({ ...prev, [key]: val }));

  const save = async (status) => {
    if (status === "submitted") {
      const ann = sections.find((s) => s.id === "annexe");
      const items = ann?.blocks?.[0]?.items || [];
      const chk = data.annexe || {};
      const missing = items.filter((it) => !chk[it]);
      if (missing.length) { toast.error(`${t("Complétez la liste de contrôle avant de soumettre.")} (${missing.length})`); return; }
    }
    setSaving(true);
    const payload = { ...meta, status: status || meta.status, data, photos };
    try {
      let newId = rid;
      if (!rid) { const res = await api.post("/monthly-reports", payload); newId = res.data.id; setRid(newId); }
      else { await api.put(`/monthly-reports/${rid}`, payload); }
      setMeta((m) => ({ ...m, status: status || m.status }));
      toast.success(t("Rapport enregistré."));
      if (isNew && newId) navigate(`/monthly/${newId}`, { replace: true });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const downloadPdf = () => {
    if (!rid) { toast.error(t("Enregistrez d'abord le rapport.")); return; }
    const token = localStorage.getItem("token");
    fetch(`${BACKEND}/api/monthly-reports/${rid}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "Rapport_Mensuel.pdf"; a.click(); URL.revokeObjectURL(u); });
  };

  return (
    <div data-testid="monthly-form-page" className="max-w-5xl">
      <button onClick={() => navigate("/monthly")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-3"><ArrowLeft className="h-4 w-4" /> {t("Retour aux rapports")}</button>
      <PageHeader eyebrow="Riposte MVE" title={isNew ? t("Nouveau rapport mensuel") : t("Rapport mensuel")} subtitle={t("Les sections se remplissent à partir des saisies quotidiennes. Complétez ce qui manque, puis soumettez au superviseur.")}>
        <Button variant="outline" className="rounded-md" onClick={downloadPdf} data-testid="mr-download"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
        <Button variant="outline" className="rounded-md" onClick={() => save("draft")} disabled={saving} data-testid="mr-save-draft">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("Enregistrer")}</Button>
        <Button className="rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" onClick={() => save("submitted")} disabled={saving} data-testid="mr-submit">{t("Soumettre")}</Button>
      </PageHeader>

      <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div><Label className="text-xs">{t("Volet")}</Label>
          <Select value={meta.volet} onValueChange={changeVolet} disabled={!isNew}>
            <SelectTrigger data-testid="mr-volet"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="epi">{t("Épidémiologiste")}</SelectItem>
              <SelectItem value="lab">{t("Technicien de laboratoire")}</SelectItem>
              <SelectItem value="generic">{t("Autre pilier")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t("Province")}</Label><Input value={meta.province} onChange={(e) => setMeta({ ...meta, province: e.target.value })} data-testid="mr-province" /></div>
        <div><Label className="text-xs">{t("Zone(s) de santé")}</Label><Input value={meta.health_zones} onChange={(e) => setMeta({ ...meta, health_zones: e.target.value })} data-testid="mr-zones" /></div>
        <div><Label className="text-xs">{t("Mois")}</Label><Input placeholder={t("Septembre")} value={meta.month} onChange={(e) => setMeta({ ...meta, month: e.target.value })} data-testid="mr-month" /></div>
        <div><Label className="text-xs">{t("Année")}</Label><Input type="number" value={meta.year} onChange={(e) => setMeta({ ...meta, year: e.target.value })} data-testid="mr-year" /></div>
        <div><Label className="text-xs">{t("Rapport N°")}</Label><Input value={meta.report_no} onChange={(e) => setMeta({ ...meta, report_no: e.target.value })} data-testid="mr-no" /></div>
      </div>

      <div className="space-y-4">
        {!isNew && (
          <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="mr-section-analysis">
            <h2 className="text-lg font-bold text-slate-900 mb-1">{t("Analyse du mois")}</h2>
            <p className="text-xs text-slate-500 mb-4">{t("Courbe épidémique, répartition par zone, matrice SMART, suivi M-1 et priorités, produits à partir des données saisies.")}</p>
            <MonthlyAnalysis reportId={id} />
          </section>
        )}
        {sections.map((sec) => (
          <section key={sec.id} className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid={`mr-section-${sec.id}`}>
            <h2 className="text-lg font-bold text-slate-900 mb-4">{sec.title}</h2>
            <div className="space-y-4">
              {sec.blocks.map((b, bi) => (
                <div key={bi}>
                  {b.type === "fields" && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {b.fields.map((f) => (
                        <div key={f.k}><Label className="text-xs">{f.label}</Label>
                          <Input type={f.type || "text"} value={(data[b.key] || {})[f.k] || ""} onChange={(e) => setD(b.key, { ...(data[b.key] || {}), [f.k]: e.target.value })} />
                        </div>
                      ))}
                    </div>
                  )}
                  {b.type === "textarea" && (
                    <div><Label className="text-xs">{b.label}</Label>
                      <Textarea rows={2} value={data[b.key] || ""} onChange={(e) => setD(b.key, e.target.value)} /></div>
                  )}
                  {b.type === "grid" && (
                    <div>{b.title && <p className="text-sm font-semibold text-slate-700 mb-2">{b.title}</p>}
                      <Grid block={b} value={data[b.key]} onChange={(v) => setD(b.key, v)} /></div>
                  )}
                  {b.type === "photos" && (
                    <div>{b.title && <p className="text-sm font-semibold text-slate-700 mb-2">{b.title}</p>}
                      <Photos photos={photos} onChange={setPhotos} /></div>
                  )}
                  {b.type === "checklist" && (
                    <div className="space-y-2">{b.items.map((it) => (
                      <label key={it} className="flex items-center gap-2.5 text-sm cursor-pointer text-slate-700">
                        <input type="checkbox" className="h-4 w-4 accent-[#F05A28]" checked={!!(data[b.key] || {})[it]}
                          onChange={(e) => setD(b.key, { ...(data[b.key] || {}), [it]: e.target.checked })} data-testid={`mr-chk-${it.slice(0, 10)}`} />
                        <span>{it}</span>
                      </label>))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        {meta.volet === "epi" && (() => {
          const AGE = ["0–4 ans", "5–14 ans", "15–49 ans", "50 ans et plus"];
          const ag = data.age_sexe || {}; const al = data.alertes || {};
          const pyr = AGE.map((g) => ({ age: g.replace(" ans", ""), Hommes: Number((ag[g] || {}).h || 0), Femmes: Number((ag[g] || {}).f || 0) }));
          const wk = ["s1", "s2", "s3", "s4"];
          const curve = ["Sem.1", "Sem.2", "Sem.3", "Sem.4"].map((w, i) => ({ sem: w, "Confirmés": Number((al["Cas confirmés"] || {})[wk[i]] || 0), Suspects: Number((al["Suspects investigués (n,%)"] || {})[wk[i]] || 0), "Prélèvements": Number((al["Prélèvements réalisés (n,%)"] || {})[wk[i]] || 0) }));
          const has = pyr.some((p) => p.Hommes || p.Femmes) || curve.some((c) => c["Confirmés"] || c.Suspects || c["Prélèvements"]);
          if (!has) return null;
          return (
            <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5" data-testid="mr-charts">
              <h2 className="text-lg font-bold text-slate-900 mb-4">{t("Aperçu graphique (auto)")}</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div><p className="text-sm font-semibold text-slate-700 mb-2">{t("Pyramide des âges et du sexe")}</p>
                  <ResponsiveContainer width="100%" height={220}><BarChart data={pyr}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="age" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend /><Bar dataKey="Hommes" fill="#1E293B" radius={[2, 2, 0, 0]} /><Bar dataKey="Femmes" fill="#F05A28" radius={[2, 2, 0, 0]} /></BarChart></ResponsiveContainer></div>
                <div><p className="text-sm font-semibold text-slate-700 mb-2">{t("Courbe épidémique hebdomadaire")}</p>
                  <ResponsiveContainer width="100%" height={220}><LineChart data={curve}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="sem" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend /><Line type="monotone" dataKey="Confirmés" stroke="#F05A28" strokeWidth={2.5} /><Line type="monotone" dataKey="Suspects" stroke="#0284C7" strokeWidth={2} /><Line type="monotone" dataKey="Prélèvements" stroke="#1E293B" strokeWidth={2} /></LineChart></ResponsiveContainer></div>
              </div>
            </section>
          );
        })()}
      </div>
    </div>
  );
}
